import { IImeiHistoryItem } from '@ltvco/refresh-lib/api';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  Stack,
  Table,
  TableContainer,
  Text,
  type ColumnValue,
  styled,
} from '@ltvco/refresh-lib/theme';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useState, type KeyboardEvent } from 'react';

import { constants } from 'appConstants';

type DeviceHistoryTableProps = {
  imeihistory?: IImeiHistoryItem[];
  /** When false, the table body starts collapsed. Defaults to true. */
  defaultExpanded?: boolean;
};

const HISTORY_HEADERS = [
  'Date',
  'Country',
  'Action',
  'By',
  'Reason Code',
  'Description',
] as const;

const HistoryTable = styled(Table)(({ theme }) => ({
  minWidth: 600,
  '& .MuiTableCell-head': {
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    py: 1.2,
    px: 2,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableCell-body': {
    fontSize: '0.82rem',
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    py: 1.2,
    px: 2,
    verticalAlign: 'middle',
  },
  '& tbody tr:hover .MuiTableCell-body': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const monoCellSx = {
  fontFamily: 'monospace',
  backgroundColor: 'background.default',
  borderRadius: 1,
  px: 1,
  py: 0.25,
  fontSize: '0.78rem',
  display: 'inline-block',
} as const;

function ActionChip({ action }: { action: string }) {
  const normalized = String(action).toLowerCase();
  const isAdded = normalized === 'added';
  const isRemoved = normalized === 'removed';

  return (
    <Chip
      label={action || '—'}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: '0.65rem',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        backgroundColor: isAdded
          ? 'error.light'
          : isRemoved
          ? 'success.light'
          : 'background.default',
        color: isAdded
          ? 'error.dark'
          : isRemoved
          ? 'success.dark'
          : 'text.secondary',
        height: 22,
      }}
    />
  );
}

function buildHistoryRows(imeihistory: IImeiHistoryItem[]): ColumnValue[][] {
  return imeihistory.map((row, index) => [
    <Text key={`date-${index}`} component="span" sx={monoCellSx}>
      {row.date || '—'}
    </Text>,
    row.Country || '—',
    <ActionChip key={`action-${index}`} action={row.action} />,
    row.by || '—',
    <Text key={`code-${index}`} component="span" sx={monoCellSx}>
      {row.reasoncode || '—'}
    </Text>,
    <Text key={`desc-${index}`} color="text.secondary">
      {row.reasoncodedesc || '—'}
    </Text>,
  ]);
}

export default function DeviceHistoryTable({
  imeihistory = [],
  defaultExpanded = true,
}: DeviceHistoryTableProps) {
  const isEmpty = imeihistory.length === 0;
  const { images } = constants;
  const [expanded, setExpanded] = useState(defaultExpanded);

  const eventCountLabel = isEmpty
    ? 'EMPTY'
    : `${imeihistory.length} EVENT${imeihistory.length !== 1 ? 'S' : ''}`;

  const handleToggle = () => setExpanded((prev) => !prev);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <Box
      id="device-fraud-history-section"
      sx={{
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2.5,
        overflow: 'hidden',
        maxWidth: 900,
        width: '100%',
        mt: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`IMEI history, ${eventCountLabel}. ${
          expanded ? 'Collapse' : 'Expand'
        } section`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        sx={{
          px: 3,
          py: 2,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Text
            variant="subtitle1"
            color="text.primary"
            sx={{ fontWeight: 700 }}
          >
            IMEI history
          </Text>
          <Chip
            label={eventCountLabel}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: '0.65rem',
              letterSpacing: '0.08em',
              backgroundColor: isEmpty ? 'background.default' : 'primary.light',
              color: isEmpty ? 'text.secondary' : 'primary.dark',
              height: 22,
            }}
          />
        </Stack>

        {expanded ? (
          <KeyboardArrowUp fontSize="small" color="action" />
        ) : (
          <KeyboardArrowDown fontSize="small" color="action" />
        )}
      </Stack>

      <Collapse in={expanded}>
        <Divider />

        {isEmpty ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ px: 3, py: 3 }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <img
                src={images.imgShieldCheckIcon}
                alt=""
                width={32}
                height={32}
              />
            </Box>
            <Box>
              <Text
                variant="body1"
                color="text.primary"
                sx={{ fontWeight: 700, mb: 0.3 }}
              >
                No status events on record
              </Text>
              <Text variant="body2" color="text.secondary">
                This device has never been flagged on the Block List or General
                List by any contributor.
              </Text>
            </Box>
          </Stack>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <HistoryTable
              size="small"
              headers={[...HISTORY_HEADERS]}
              rows={buildHistoryRows(imeihistory)}
            />
          </TableContainer>
        )}
      </Collapse>
    </Box>
  );
}
